import type { Mailbox, MailboxDomainSummary } from '@/features/mailboxes/types';

export interface MailboxGroup {
  summary: MailboxDomainSummary;
  /** Mailboxes of this domain that were loaded (and match the search), A→Z. */
  rows: Mailbox[];
}

const byAddress = (a: Mailbox, b: Mailbox) => a.address.localeCompare(b.address);
const domainOf = (address: string) => address.slice(address.lastIndexOf('@') + 1).toLowerCase();

/**
 * Group loaded mailboxes under the tenant's domains. Rows are matched by
 * domainId, falling back to the address domain for responses that predate the
 * domainId field. With a search term, only groups with at least one match are
 * returned; without one every domain is returned, including empty ones. A
 * mailbox whose domain is not in the summary still appears (in its own group),
 * so nothing loaded is ever hidden.
 */
export function groupMailboxes(summary: MailboxDomainSummary[], mailboxes: Mailbox[], term = ''): MailboxGroup[] {
  const needle = term.trim().toLowerCase();
  const matches = (m: Mailbox) => !needle || m.address.toLowerCase().includes(needle) || (m.displayName ?? '').toLowerCase().includes(needle);
  const byId = new Map(summary.map((s) => [s.domainId, s]));
  const byName = new Map(summary.map((s) => [s.domain.toLowerCase(), s]));
  const groups = new Map<string, MailboxGroup>(summary.map((s) => [s.domainId, { summary: s, rows: [] }]));

  for (const m of mailboxes) {
    if (!matches(m)) continue;
    const s = (m.domainId && byId.get(m.domainId)) || byName.get(domainOf(m.address));
    const key = s?.domainId ?? `unknown:${domainOf(m.address)}`;
    let g = groups.get(key);
    if (!g) {
      g = { summary: { domainId: key, domain: domainOf(m.address), mailEnabled: false, status: 'removed', mailboxCount: 0, usedBytes: 0, quotaBytes: 0 }, rows: [] };
      groups.set(key, g);
    }
    g.rows.push(m);
  }
  const out = [...groups.values()];
  for (const g of out) {
    g.rows.sort(byAddress);
    // Groups synthesised from rows have no server counts: derive them.
    if (g.summary.status === 'removed' && g.summary.mailboxCount === 0) {
      g.summary = { ...g.summary, mailboxCount: g.rows.length, usedBytes: g.rows.reduce((n, r) => n + r.usedBytes, 0), quotaBytes: g.rows.reduce((n, r) => n + r.quotaBytes, 0) };
    }
  }
  return out
    .filter((g) => !needle || g.rows.length > 0)
    .sort((a, b) => a.summary.domain.localeCompare(b.summary.domain));
}
